import { useMutation } from 'react-query';
import { useApi } from '../../core/api/api.context';
import {
  CreateNoteDto,
  EditNoteDto,
  GroundfloorManagementAPIs,
  KnownNoteAttachedToDtoType,
  NoteDto,
  PaginatedDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { OperationOptions, RestError } from '@azure/core-http';

export interface CreateNote {
  nodeType: KnownNoteAttachedToDtoType;
  nodeId: string;
  content: string;
  isPrivate: boolean;
}

export function useMutationCreateNote() {
  const api = useApi();
  return useMutation<unknown, RestError, CreateNote>(async (dto) => {
    const controller = getController(api, dto.nodeType);
    await controller.create(dto.nodeId, {
      content: dto.content,
      isPrivate: dto.isPrivate
    });
  }, {});
}

function getController(api: GroundfloorManagementAPIs, nodeType: KnownNoteAttachedToDtoType): NoteController {
  if (!nodeType) {
    throw new Error('Node type is required');
  }

  switch (nodeType) {
    case KnownNoteAttachedToDtoType.Hub:
      return api.hubsNotesController as NoteController;
    case KnownNoteAttachedToDtoType.Project:
      return api.projectsNotesController as NoteController;
    case KnownNoteAttachedToDtoType.Group:
      return api.groupsNotesController as NoteController;
    case KnownNoteAttachedToDtoType.Delivery:
      return api.deliveriesNotesController as NoteController;
    case KnownNoteAttachedToDtoType.User:
      return api.usersNotesController as NoteController;
    default:
      throw new Error('Invalid node type');
  }
}

export function getRetailController(
  api: GroundfloorManagementAPIs,
  nodeType: KnownNoteAttachedToDtoType
): RetailNoteController {
  if (!nodeType) {
    throw new Error('Node type is required');
  }

  switch (nodeType) {
    case KnownNoteAttachedToDtoType.Order:
      return api.ordersNotesController as RetailNoteController;
    default:
      throw new Error('Invalid node type');
  }
}
export interface NoteController {
  findAll(attachedToId: string, options?: OperationOptions): Promise<PaginatedDto & { items?: NoteDto[] }>;
  create(attachedToId: string, body: CreateNoteDto, options?: OperationOptions): Promise<NoteDto>;
  findOne(attachedToId: string, noteID: string, options?: OperationOptions): Promise<NoteDto>;
  edit(attachedToId: string, noteID: string, body: EditNoteDto, options?: OperationOptions): Promise<NoteDto>;
  delete(attachedToId: string, noteID: string, options?: OperationOptions): Promise<void>;
}

export interface RetailNoteController {
  findAll(
    attachedToId: string,
    retailerId: string,
    options?: OperationOptions
  ): Promise<PaginatedDto & { items?: NoteDto[] }>;
  create(attachedToId: string, retailerId: string, body: CreateNoteDto, options?: OperationOptions): Promise<NoteDto>;
  findOne(attachedToId: string, noteID: string, retailerId: string, options?: OperationOptions): Promise<NoteDto>;
  edit(
    attachedToId: string,
    noteID: string,
    retailerId: string,
    body: EditNoteDto,
    options?: OperationOptions
  ): Promise<NoteDto>;
  delete(attachedToId: string, noteID: string, retailerId: string, options?: OperationOptions): Promise<void>;
}
